import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, ArrowUpRight, BookMarked, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATES = ["Idea", "Questionario", "Struttura", "Scrittura", "Revisione", "Copertina", "Impaginazione", "Pubblicazione", "Amazon", "Royalty"];
const GENRES = ["Narrativa", "Saggistica", "Biografia / Memoir", "Business", "Crescita personale", "Poesia", "Bambini", "Manuale / How-to", "Altro"];

const StatusPill = ({ status }) => {
  const idx = STATES.indexOf(status);
  const pct = Math.round(((idx + 1) / STATES.length) * 100);
  return (
    <div className="flex items-center gap-2">
      <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#D4AF37]">
        {status}
      </span>
      <span className="text-xs text-white/30">{pct}%</span>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [books, setBooks] = useState(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState({});

  const load = async () => {
    try {
      const [b, u] = await Promise.all([api.get("/books"), api.get("/unread")]);
      setBooks(b.data.books);
      setUnread(u.data.books || {});
    } catch {
      setBooks([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createBook = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/books", { title: title || "Nuovo progetto", genre: genre || null });
      toast.success("Progetto creato! Iniziamo il questionario.");
      setOpen(false);
      setTitle("");
      setGenre("");
      navigate(`/books/${data.book_id}/questionario`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Dashboard</p>
            <h1 className="mt-3 font-serif text-4xl font-light text-white sm:text-5xl">
              Ciao, {user?.name?.split(" ")[0] || "autore"}.
            </h1>
            <p className="mt-2 text-white/50">I tuoi progetti editoriali, tutti in un posto.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-book-btn" className="gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                <Plus className="h-4 w-4" /> Nuovo libro
              </Button>
            </DialogTrigger>
            <DialogContent className="border-white/10 bg-[#0A1124] text-white">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-light">Un nuovo progetto</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-2">
                <div>
                  <Label className="text-white/70">Titolo provvisorio</Label>
                  <Input
                    data-testid="new-book-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Es. Il mio viaggio"
                    className="mt-1.5 border-white/10 bg-white/5 text-white placeholder:text-white/30"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Genere</Label>
                  <Select value={genre} onValueChange={setGenre}>
                    <SelectTrigger data-testid="new-book-genre-select" className="mt-1.5 border-white/10 bg-white/5 text-white">
                      <SelectValue placeholder="Scegli un genere" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0A1124] text-white">
                      {GENRES.map((g) => (
                        <SelectItem key={g} value={g} className="focus:bg-white/5 focus:text-white">
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={createBook}
                  disabled={busy}
                  data-testid="create-book-confirm-btn"
                  className="rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"
                >
                  {busy ? "Creazione…" : "Crea e inizia"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Questionnaire reminder banner */}
        {Array.isArray(books) && books.length > 0 && (() => {
          const pending = books.find((b) => !b.questionnaire_completed);
          if (!pending) return null;
          return (
            <motion.button
              type="button"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => navigate(`/books/${pending.book_id}/questionario`)}
              data-testid="questionnaire-reminder-banner"
              className="mt-8 flex w-full items-start gap-4 rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/15 via-[#D4AF37]/5 to-transparent p-5 text-left transition-all hover:border-[#D4AF37]/70 hover:from-[#D4AF37]/25"
            >
              <span className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[#D4AF37]/20">
                <AlertCircle className="h-5 w-5 text-[#D4AF37]" />
              </span>
              <span className="flex flex-1 flex-col gap-1">
                <span className="font-serif text-lg font-light text-white">
                  Completa il questionario per <span className="text-[#D4AF37]">{pending.title}</span>
                </span>
                <span className="text-sm text-white/60">
                  È il primo passo per farci lavorare al tuo libro. Ci vogliono pochi minuti.
                </span>
              </span>
              <span className="mt-1 hidden shrink-0 rounded-full border border-[#D4AF37]/60 px-4 py-1.5 text-xs font-medium text-[#D4AF37] sm:inline-block">
                Riprendi
              </span>
            </motion.button>
          );
        })()}

        {/* Books grid */}
        <div className="mt-12">
          {books === null ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-40 animate-pulse rounded-2xl border border-white/5 bg-[#0A1124]" />
              ))}
            </div>
          ) : books.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/10 bg-[#0A1124] py-24 text-center">
              <BookMarked className="h-10 w-10 text-[#D4AF37]" />
              <p className="mt-6 font-serif text-2xl font-light text-white">Nessun libro, ancora.</p>
              <p className="mt-2 max-w-sm text-white/40">Crea il tuo primo progetto e inizia il questionario guidato.</p>
              <Button
                onClick={() => setOpen(true)}
                data-testid="empty-new-book-btn"
                className="mt-8 gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]"
              >
                <Plus className="h-4 w-4" /> Crea il primo libro
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {books.map((b, i) => (
                <motion.button
                  key={b.book_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  onClick={() => navigate(`/books/${b.book_id}`)}
                  data-testid={`book-card-${b.book_id}`}
                  className="group flex flex-col rounded-2xl border border-white/5 bg-[#0A1124] p-6 text-left transition-all duration-300 hover:-translate-y-1 hover:border-white/10"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-white/5 bg-[#050A15] font-serif text-xl text-[#D4AF37]">
                      {b.title?.[0]?.toUpperCase() || "L"}
                    </div>
                    <div className="flex items-center gap-2">
                      {unread[b.book_id]?.total > 0 && (
                        <span
                          data-testid={`unread-badge-${b.book_id}`}
                          className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#D4AF37] px-1.5 text-[11px] font-semibold text-[#050A15]"
                        >
                          {unread[b.book_id].total}
                        </span>
                      )}
                      <ArrowUpRight className="h-5 w-5 text-white/20 transition-colors group-hover:text-[#D4AF37]" />
                    </div>
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-light leading-tight text-white line-clamp-2">
                    {b.title}
                  </h3>
                  {b.genre && <p className="mt-1 text-sm text-white/40">{b.genre}</p>}
                  <div className="mt-6">
                    <StatusPill status={b.status} />
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
